import { useEffect, useState } from 'react'
import useApi from '../../hooks/useApi'
import useCleanup from '../../hooks/useCleanup'
import { getApiErrorMessage } from '../../lib/utils'
import { Button, InlineAlert, LoadingSpinner } from '../../components/BaseComponents'
import UpgradeRequestBanner from './UpgradeRequestBanner'

type UpgradeRequest = {
	userId: string
	requestedAt: string
	message: string | null
}

function AdminCompanyUpgradeRequests({ companyId }: { companyId: string }) {
	const [requests, setRequests] = useState<UpgradeRequest[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState('')
	const [loadedCompanyId, setLoadedCompanyId] = useState<string>()
	const [loadedApi, setLoadedApi] = useState<ReturnType<typeof useApi>>()
	const api = useApi()

	const loadRequests = useCleanup(() => {
		let cancelled = false
		setLoadedCompanyId(companyId)
		setLoadedApi(api)
		setRequests([])
		setError('')
		setLoading(true)
		const run = async () => {
			try {
				const result = await api.get(`admin/company/${companyId}/summary`)
				if (!cancelled) {
					setRequests(result.data?.upgradeRequests ?? [])
				}
			} catch (err) {
				if (!cancelled) setError(getApiErrorMessage(err, 'Could not load upgrade requests. Please try again.'))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	})

	useEffect(() => {
		loadRequests()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId, api])

	if (loadedCompanyId !== companyId || loadedApi !== api || loading) {
		return <LoadingSpinner inline loadingText='Loading upgrade requests...' />
	}
	if (error) {
		return (
			<InlineAlert type='error'>
				<p>{error}</p>
				<Button type='Primary' text='Retry upgrade requests' onClick={loadRequests} />
			</InlineAlert>
		)
	}

	return (
		<>
			{requests.map((request) => (
				<UpgradeRequestBanner key={request.userId} message={request.message} />
			))}
		</>
	)
}

export default AdminCompanyUpgradeRequests
